import styles from "./MessageTab.module.scss";
import classNames from "classnames/bind";
import { useEffect, useState, useRef } from "react";
import PrimaryButton from "~/components/common/primary-button/PrimaryButton";
import PrimaryInput from "~/components/common/primary-input/PrimaryInput";
import smsApi, { ISmsMessage } from "~/apis/sms";
import { notify } from "~/utils/helper";
import LoadingMainContent from "~/components/common/loading-main-content/LoadingMainContent";

const cx = classNames.bind(styles);

interface MessageTabProps {
  jobId?: string;
  primaryPhone?: string;
  secondaryPhone?: string;
}

const MessageTab = ({ jobId, primaryPhone, secondaryPhone }: MessageTabProps) => {
  const [message, setMessage] = useState("");
  const [messages, setMessages] = useState<ISmsMessage[]>([]);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const formatTime = (date: string | undefined) => {
    if (!date) return "";
    return new Date(date).toLocaleString("en-US", {
      month: "short",
      day: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Fetch conversations and messages when component mounts
  useEffect(() => {
    const fetchMessages = async () => {
      if (!jobId) return;

      setIsLoading(true);
      try {
        // Get conversations for this job
        const conversationsResponse = await smsApi.getConversations({
          page: 1,
          limit: 100,
        });

        if (conversationsResponse.data?.data?.conversations) {
          // Find all conversations matching this job or phone numbers
          const matchingConversations = conversationsResponse.data.data.conversations.filter(
            (conv) => {
              const phoneMatch =
                conv.phoneNumber === primaryPhone ||
                conv.phoneNumber === secondaryPhone;
              const jobMatch = conv.jobId == jobId; // Use == for type coercion

              return phoneMatch || jobMatch;
            }
          );

          // Priority: conversation with messages > most recent conversation
          const conversation =
            matchingConversations.find((conv) => conv.lastMessage) ||
            matchingConversations[0];

          if (conversation) {
            setConversationId(conversation.id);

            // Fetch messages for this conversation
            const messagesResponse = await smsApi.getMessages(conversation.id, {
              page: 1,
              limit: 100,
            });

            if (messagesResponse.data?.data?.messages) {
              setMessages(messagesResponse.data.data.messages);
            }
          }
        }
      } catch (error: any) {
        console.error("Failed to fetch messages:", error);
        // Don't show error notification on initial load if no messages exist yet
      } finally {
        setIsLoading(false);
      }
    };

    fetchMessages();
  }, [jobId, primaryPhone, secondaryPhone]);

  const handleSendMessage = async () => {
    if (!message.trim()) {
      notify("error", "Please enter a message");
      return;
    }

    // Determine which phone number to use
    const toPhone = primaryPhone || secondaryPhone;
    if (!toPhone) {
      notify("error", "No phone number available for this contact");
      return;
    }

    setIsSending(true);
    try {
      const payload = {
        to: toPhone,
        body: message.trim(),
        conversationId: conversationId || undefined,
        jobId: jobId,
      };

      const response = await smsApi.sendSms(payload);

      if (response.data?.data) {
        notify("success", "Message sent successfully!");
        setMessage("");

        // If we didn't have a conversation ID before, get it now
        if (!conversationId && response.data.data.conversationId) {
          setConversationId(response.data.data.conversationId);
        }

        // Refresh messages
        if (conversationId || response.data.data.conversationId) {
          const messagesResponse = await smsApi.getMessages(
            conversationId || response.data.data.conversationId,
            { page: 1, limit: 100 }
          );

          if (messagesResponse.data?.data?.messages) {
            setMessages(messagesResponse.data.data.messages);
          }
        }
      }
    } catch (error: any) {
      console.error("Failed to send message:", error);
      notify(
        "error",
        `Failed to send message: ${error?.response?.data?.message || "Unknown error"}`
      );
    } finally {
      setIsSending(false);
    }
  };

  if (isLoading) {
    return (
      <div className={cx("message-tab-container")}>
        <LoadingMainContent />
      </div>
    );
  }

  return (
    <div className={cx("message-tab-container")}>
      <div className={cx("message-body")}>
        {messages.length === 0 ? (
          <div className={cx("empty-state")}>
            <p>No messages yet. Start a conversation!</p>
          </div>
        ) : (
          messages.map((msg) => (
            <div
              className={cx("message-item", {
                outbound: msg.direction === "outbound",
                inbound: msg.direction === "inbound",
              })}
              key={msg.id}
            >
              <div className={cx("message-item-header")}>
                <h3 className={cx("message-item-title")}>
                  {msg.direction === "outbound"
                    ? "You"
                    : msg.fromNumber || "Customer"}
                </h3>

                <p className={cx("message-item-time")}>
                  {formatTime(msg.sentAt || msg.createdAt)}
                </p>
              </div>
              <p className={cx("message-item-content")}>{msg.body}</p>
              <div
                className={cx(
                  "message-item-type",
                  msg.direction === "outbound" ? "update" : "customer"
                )}
              >
                {msg.direction === "outbound" ? "sent" : "received"}
                {msg.status === "delivered" && " ✓✓"}
                {msg.status === "failed" && " ✗"}
              </div>
            </div>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      <div className={cx("footer")}>
        <PrimaryInput
          placeholder="Type a message..."
          name="message"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          className={cx("message-input")}
          disabled={isSending || (!primaryPhone && !secondaryPhone)}
        />
        <PrimaryButton
          title={isSending ? "Sending..." : "Send"}
          onClick={handleSendMessage}
          className={cx("add-message-button")}
          disabled={isSending || !message.trim() || (!primaryPhone && !secondaryPhone)}
        />
      </div>
    </div>
  );
};

export default MessageTab;
